import { useMemo } from "react"

import { useTranslation } from "react-i18next"

import type { TokenInfo } from "../../utils/activity-message-utils"

import { Cpu, FileSearch } from "@/components/icons"
import { useCountUp } from "@/hooks/use-count-up"
import { cn } from "@/lib/utils"

type ActivityStatsHeaderProps = {
  tokenInfo?: TokenInfo
  sourcesCount?: number
}

export function ActivityStatsHeader({ tokenInfo, sourcesCount }: ActivityStatsHeaderProps) {
  const { t } = useTranslation()

  const numberFormatter = useMemo(() => new Intl.NumberFormat(undefined), [])

  const totalTokens = tokenInfo?.total_tokens ?? 0
  const sources = sourcesCount ?? 0

  // Animated counts
  const animatedTokens = useCountUp({
    end: totalTokens,
    duration: 800,
    easing: (t: number) => 1 - Math.pow(1 - t, 3), // ease-out cubic for smoother animation
  })

  const animatedSources = useCountUp({
    end: sources,
    duration: 600,
    easing: (t: number) => 1 - Math.pow(1 - t, 3),
  })

  return (
    <div className="py-2">
      <div className="from-background to-muted/10 relative overflow-hidden rounded-xl bg-gradient-to-br shadow-sm backdrop-blur-sm">
        {/* Content */}
        <div className="relative flex flex-wrap items-center justify-between gap-y-4 px-6 py-4">
          {/* Center section - Total Tokens */}
          <div className="flex items-center gap-3">
            <div className="bg-accent/10 flex size-10 items-center justify-center rounded-full">
              <Cpu className="text-primary/60 size-4" />
            </div>
            <div className="flex flex-col">
              <div className="flex items-baseline gap-1">
                <span className="text-foreground text-lg font-semibold tracking-tight tabular-nums">
                  {numberFormatter.format(Math.max(0, Math.round(animatedTokens)))}
                </span>
              </div>
              <span className="text-muted-foreground/70 text-xs font-medium">
                {t("activity_stats.total_tokens")}
              </span>
            </div>
          </div>

          {/* Right divider */}
          <div className="via-border/40 h-12 w-px bg-gradient-to-b from-transparent to-transparent" />

          {/* Right section - Sources */}
          <div className="flex items-center gap-3">
            <div className="bg-secondary/10 flex size-10 items-center justify-center rounded-full">
              <FileSearch className="text-primary/80 size-4" />
            </div>
            <div className="flex flex-col">
              <div className="flex items-baseline gap-1">
                <span
                  className={cn(
                    "text-foreground text-lg font-semibold tracking-tight tabular-nums"
                  )}
                >
                  {Math.round(animatedSources)}
                </span>
              </div>
              <span className="text-muted-foreground/70 text-xs font-medium">
                {Math.round(animatedSources) === 1
                  ? t("activity_stats.source")
                  : t("activity_stats.sources")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
